import { motion } from 'framer-motion';

interface MobileSection {
  id: string;
  title?: string;
  body: string;
}

interface DoubleMobileScrollProps {
  title: string;
  media: string[];
  caption?: string;
  sections: MobileSection[];
}

const isVideo = (src: string) => src.endsWith('.mp4') || src.endsWith('.webm');

function PhoneFrame({ src, alt }: { src: string; alt: string }) {
  return (
    <div className="mx-auto w-full max-w-[20rem] rounded-[2.4rem] border border-black/10 bg-neutral-950 p-3 shadow-[0_32px_80px_rgba(0,0,0,0.14)]">
      <div className="mb-3 mx-auto h-1.5 w-16 rounded-full bg-white/15" />
      <div className="overflow-hidden rounded-[1.8rem] bg-black aspect-[9/19]">
        {isVideo(src) ? (
          <video
            src={src}
            className="h-full w-full object-cover"
            autoPlay
            loop
            muted
            playsInline
            preload="metadata"
            aria-label={alt}
          />
        ) : (
          <img
            src={src}
            alt={alt}
            className="h-full w-full object-cover"
            loading="lazy"
          />
        )}
      </div>
    </div>
  );
}

export function DoubleMobileScroll({
  title,
  media,
  caption,
  sections,
}: DoubleMobileScrollProps) {
  const featuredMedia = media.slice(0, 3);

  return (
    <section className="grid gap-10 md:gap-12 lg:grid-cols-[minmax(0,0.9fr)_minmax(320px,0.7fr)] lg:gap-14">
      <div className="space-y-10 md:space-y-12">
        {sections.map(section => (
          <motion.article
            key={section.id}
            initial={{ opacity: 0, y: 28 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.35 }}
            transition={{ duration: 0.65, ease: [0.22, 1, 0.36, 1] }}
            className="flex min-h-[28vh] items-center md:min-h-[24vh] lg:min-h-[28vh]"
          >
            <div className="max-w-xl space-y-4">
              {section.title ? (
                <h3 className="heading-section">
                  {section.title}
                </h3>
              ) : null}
              <div
                className="body [&_strong]:font-medium [&_strong]:text-black"
                dangerouslySetInnerHTML={{ __html: section.body }}
              />
            </div>
          </motion.article>
        ))}
      </div>

      <div className="lg:sticky lg:top-24 lg:self-start">
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          className="rounded-[2.5rem] border border-black/6 bg-black/[0.025] p-4 md:p-6 backdrop-blur-sm"
        >
          <div
            className={`grid gap-4 ${
              featuredMedia.length === 1
                ? 'grid-cols-1'
                : 'grid-cols-1 md:grid-cols-2 xl:grid-cols-2'
            }`}
          >
            {featuredMedia.map((item, index) => (
              <PhoneFrame
                key={`${item}-${index}`}
                src={item}
                alt={`${title} mobile screen ${index + 1}`}
              />
            ))}
          </div>
          {caption ? (
            <p className="label mt-4 md:mt-6 text-center">
              {caption}
            </p>
          ) : null}
        </motion.div>
      </div>
    </section>
  );
}
